import { useMemo, useState } from "react"
import { useNavigate } from "@tanstack/react-router"
import {
	IconHashtagOutline18 as Hash,
	IconMagnifierOutline18 as Search,
} from "nucleo-ui-outline-18"

import { Button, Input, Popover } from "@productlane/ui"

import type { BroadcastChannel } from "./slack-broadcast-channels"
import { api } from "@/lib/api"
import { useSlackConnections } from "@/lib/zero"
import { groupBroadcastChannels } from "./slack-broadcast-channels"

// Past this many channels the list gets a search field.
const SEARCH_FROM = 8

/**
 * "N Slack channels" in the publish dialog: opens the list of channels the
 * changelog posts to, with a way to the Channels page where Changelog is set.
 */
export function SlackBroadcastChannelsPopover({
	channels,
	children,
}: {
	channels: readonly BroadcastChannel[]
	children: React.ReactNode
}) {
	const navigate = useNavigate()
	const connections = useSlackConnections()
	const [query, setQuery] = useState("")
	const teams = api.workspace.slackTeams.useQuery(undefined, {
		enabled: connections.length > 1,
		staleTime: 60 * 60_000,
	})

	const groups = useMemo(
		() => groupBroadcastChannels(channels, connections, query),
		[channels, connections, query],
	)
	const workspaceName = (id: string, fallback: string) =>
		teams.data?.find((team) => team.id === id)?.name ?? fallback

	return (
		<Popover
			align="start"
			className="w-80 p-0"
			content={
				<div className="flex max-h-80 flex-col">
					{channels.length > SEARCH_FROM && (
						<div className="border-b border-gray-200 p-2 dark:border-gray-700/80">
							<Input
								name="broadcast-channel-search"
								placeholder="Search channels"
								icon={Search}
								value={query}
								onChange={(e) => setQuery(e.target.value)}
							/>
						</div>
					)}
					<div className="min-h-0 flex-1 overflow-y-auto p-1.5 pb-2">
						{groups.map((group) => (
							<div key={group.slackConnectSettingsId}>
								<div className="flex h-7 items-center justify-between gap-2 px-1.5 text-xs font-medium text-tertiary">
									<span className="truncate">
										{workspaceName(
											group.slackConnectSettingsId,
											group.workspaceName,
										)}
									</span>
									<Button
										variant="link"
										size="sm"
										className="flex-none"
										onClick={() =>
											void navigate({
												to: "/settings/integrations/slack/$connectionId/channels",
												params: { connectionId: group.slackConnectSettingsId },
											})
										}
									>
										Edit channels
									</Button>
								</div>
								{group.channels.map((channel) => (
									<div
										key={channel.id}
										className="flex h-7 items-center gap-2 rounded-md px-1.5 text-sm text-primary"
									>
										<Hash className="size-3.5 flex-none text-tertiary" />
										<span className="truncate">{channel.name}</span>
									</div>
								))}
							</div>
						))}
						{groups.length === 0 && (
							<div className="px-1.5 py-2 text-sm text-tertiary">
								No channels match &quot;{query.trim()}&quot;
							</div>
						)}
					</div>
				</div>
			}
		>
			{children}
		</Popover>
	)
}
